import { supabaseStorageCompat as supabase } from "./fileStorage";

import { db } from "./dbClient";

// ─── CaseDetail.jsx ───────────────────────────────────────────────────────────
// Supabase-backed case detail view.
//
// Case lifecycle:
//   open  →  [submit for review]  →  pending_review  →  [reviewer approves]
//         →  closed | escalated
//
// Who can do what:
//   Submitter:  can close/escalate, assign, upload docs — UNTIL submitted
//               after submit → case is LOCKED (read-only) for them
//   Reviewer:   sees 🔔 banner, can Approve / Deny / Request Changes
//               after approval → case moves to closed/escalated (COMPLETE)
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { callAI } from "./aiClient";
import { useAuditLog } from "./useAuditLog";
import { useToast, ToastContainer } from "./Toast";

// Normalize team members — Supabase uses first_name, App.jsx uses firstName
function norm(m) {
  if (!m) return m;
  return {
    ...m,
    first_name: m.first_name || m.firstName || "",
    last_name:  m.last_name  || m.lastName  || "",
    email:      m.email      || "",
    role:       m.role       || "",
  };
}

import { useTheme } from "./theme";
import ExternalResearch from "./ExternalResearch";
import { DeadlineDisplay } from "./DeadlineDisplay";
import InvestigationSources from "./InvestigationSources";

// ─── EmailJS ──────────────────────────────────────────────────────────────────
async function sendReviewEmail({ reviewer, submittedBy, caseData, action, notes }) {
  const serviceId  = import.meta.env.VITE_EMAILJS_SERVICE_ID;
  const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
  const publicKey  = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

  const ok = v => v && v !== "undefined" && v.trim() !== "";
  if (!ok(serviceId) || !ok(templateId) || !ok(publicKey)) {
    console.info("[EmailJS] Not configured — skipping.");
    return { skipped: true };
  }
  if (!reviewer?.email) { console.warn("[EmailJS] No reviewer email."); return { skipped: true }; }

  const res = await fetch("https://api.emailjs.com/api/v1.0/email/send", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      service_id: serviceId, template_id: templateId, user_id: publicKey,
      template_params: {
        to_email:    reviewer.email,
        to_name:     `${reviewer.first_name} ${reviewer.last_name}`,
        from_name:   submittedBy ? `${submittedBy.first_name} ${submittedBy.last_name}` : "A colleague",
        case_id:     caseData.id,
        case_entity: caseData.entity,
        action:      action === "escalate" ? "ESCALATE" : "CLOSE",
        notes,
        app_url:     window.location.origin,
      },
    }),
  });
  if (!res.ok) throw new Error(`EmailJS ${res.status}`);
  return { sent: true };
}

// ─── Supabase Storage doc helpers ─────────────────────────────────────────────
// Docs are stored in the 'case-documents' private bucket.
// Metadata (name, size, path) is stored in the case_documents table.

// ─── Sub-components ───────────────────────────────────────────────────────────
function Badge({ color, children }) {
  return (
    <span style={{
      color, fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
      background: `${color}18`, border: `1px solid ${color}30`,
      padding: "2px 8px", borderRadius: 4, fontFamily: "'DM Mono', monospace",
    }}>{children}</span>
  );
}

function Section({ title, children, action, dim }) {
  const C = useTheme();
  return (
    <div style={{
      background: C.surface, border: `1px solid ${dim ? C.border + "80" : C.border}`,
      borderRadius: 10, padding: 20, marginBottom: 16,
      opacity: dim ? 0.6 : 1, transition: "opacity 0.2s",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.1em", fontFamily: "'DM Mono', monospace" }}>{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

// ─── Locked banner ────────────────────────────────────────────────────────────
function LockedBanner({ review, reviewer }) {
  const C = useTheme();
  const { t } = useTranslation();
  return (
    <div style={{
      background: `${C.gold}0A`, border: `1px solid ${C.gold}35`,
      borderRadius: 10, padding: "14px 18px", marginBottom: 16,
      display: "flex", alignItems: "center", gap: 12,
    }}>
      <span style={{ fontSize: 20 }}>🔒</span>
      <div>
        <div style={{ color: C.gold, fontSize: 13, fontWeight: 700 }}>
          {t('caseDetail.awaitingReview')}
        </div>
        <div style={{ color: C.textDim, fontSize: 12, marginTop: 3, lineHeight: 1.5 }}>
          {t('caseDetail.submittedFor')}{" "}
          <span style={{ color: review?.action === "escalate" ? C.danger : C.success, fontWeight: 600 }}>
            {review?.action}
          </span>{" "}
          on {review?.submitted_at ? new Date(review.submitted_at).toLocaleString() : "—"}.
          {reviewer && (
            <> {t('caseDetail.waitingFor', { name: `${reviewer.first_name} ${reviewer.last_name}` })}</>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Complete banner ──────────────────────────────────────────────────────────
function CompleteBanner({ caseData, closedBy }) {
  const C = useTheme();
  const { t } = useTranslation();
  const isEscalated = caseData.status === "escalated";
  const color = isEscalated ? C.danger : C.success;
  const title = isEscalated
    ? t('caseDetail.caseEscalated')
    : caseData.status === "merged"
    ? t('caseDetail.caseMerged')
    : t('caseDetail.caseClosed');
  return (
    <div style={{
      background: `${color}0A`, border: `1px solid ${color}35`,
      borderRadius: 10, padding: "14px 18px", marginBottom: 16,
      display: "flex", alignItems: "center", gap: 12,
    }}>
      <span style={{ fontSize: 20 }}>{isEscalated ? "⬆" : "✓"}</span>
      <div>
        <div style={{ color, fontSize: 13, fontWeight: 700 }}>
          {title}
        </div>
        <div style={{ color: C.textDim, fontSize: 12, marginTop: 3 }}>
          {closedBy && <>{t('caseDetail.approvedBy', { name: `${closedBy.first_name} ${closedBy.last_name}` })} </>}
          {caseData.closed_at ? new Date(caseData.closed_at).toLocaleString() : ""}
        </div>
        {caseData.review_notes && (
          <div style={{ color: C.textMid, fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>
            {t('caseDetail.reviewNotes')} {caseData.review_notes}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PDF Upload Panel ─────────────────────────────────────────────────────────
function DocumentsPanel({ caseId, companyId, currentUser, locked, onDocsChange }) {
  const C = useTheme();
  const [docs, setDocs]           = useState([]);
  const [dragging, setDragging]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  // Load doc metadata from Supabase on mount
  useEffect(() => {
    if (!caseId || !companyId) return;
    db.from("case_documents")
      .select("*")
      .eq("case_id", caseId)
      .eq("company_id", companyId)
      .order("created_at", { ascending: true })
      .then(({ data }) => {
        const loaded = data || [];
        setDocs(loaded);
        onDocsChange?.(loaded);
      });
  }, [caseId, companyId]);

  async function handleFiles(files) {
    if (locked) return;
    const pdfs = Array.from(files).filter(f => f.type === "application/pdf" || f.type === "");
    if (!pdfs.length) return;
    setUploading(true);
    const newDocs = [];
    for (const file of pdfs) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const storagePath = `${companyId}/${caseId}/${Date.now()}_${safeName}`;
      const { error: uploadErr } = await supabase
        .from("case-documents")
        .upload(storagePath, file, { contentType: file.type || "application/pdf", upsert: false });
      if (uploadErr) { console.warn("doc upload failed:", uploadErr.message); continue; }
      const { data: meta, error: metaErr } = await db.from("case_documents").insert({
        case_id: caseId,
        company_id: companyId,
        name: file.name,
        size: file.size,
        storage_path: storagePath,
        uploaded_by: currentUser?.email,
      }).single().execute();
      if (!metaErr && meta) newDocs.push(meta);
    }
    const updated = [...docs, ...newDocs];
    setDocs(updated);
    onDocsChange?.(updated);
    setUploading(false);
  }

  async function removeDoc(id) {
    if (locked) return;
    const doc = docs.find(d => d.id === id);
    if (doc?.storage_path) {
      await supabase.from("case-documents").remove([doc.storage_path]);
    }
    await db.from("case_documents").delete().eq("id", id).execute();
    const updated = docs.filter(d => d.id !== id);
    setDocs(updated);
    onDocsChange?.(updated);
  }

  async function openDoc(doc) {
    if (doc.storage_path) {
      const result = await db.storage
        .from("case-documents")
        .createSignedUrl(doc.storage_path, 300); // 5-min signed URL
      if (result?.data?.signedUrl) window.open(result.data.signedUrl, "_blank");
    }
  }

  const { t } = useTranslation();
  return (
    <Section title={t('caseDetail.sectionDocuments')} dim={locked}>
      {!locked && (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? C.accent : C.border}`,
            borderRadius: 8, padding: "18px 16px", textAlign: "center",
            cursor: "pointer", marginBottom: docs.length ? 14 : 0,
            background: dragging ? C.accentGlow : "transparent", transition: "all 0.15s",
          }}>
          <input ref={inputRef} type="file" accept=".pdf" multiple style={{ display: "none" }}
            onChange={e => handleFiles(e.target.files)} />
          <div style={{ fontSize: 20, marginBottom: 4 }}>📎</div>
          <div style={{ color: dragging ? C.accent : C.textMid, fontSize: 13 }}>
            {uploading ? t('caseDetail.docUploading') : t('caseDetail.docDropZone')}
          </div>
          <div style={{ color: C.textDim, fontSize: 11, marginTop: 3 }}>{t('caseDetail.docPdfOnly')}</div>
        </div>
      )}
      {docs.map(doc => (
        <div key={doc.id} style={{
          display: "flex", alignItems: "center", gap: 12, padding: "10px 12px",
          background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, marginTop: 8,
        }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>📄</span>
          <div style={{ flex: 1, overflow: "hidden" }}>
            <div style={{ color: C.text, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.name}</div>
            <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>
              {(doc.size / 1024).toFixed(1)} KB · {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : "—"}
            </div>
          </div>
          <button onClick={() => openDoc(doc)} style={{ background: C.accentGlow, border: `1px solid ${C.borderLight}`, borderRadius: 5, color: C.accent, padding: "4px 10px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace" }}>{t('caseDetail.docView')}</button>
          {!locked && (
            <button onClick={() => removeDoc(doc.id)} style={{ background: "transparent", border: `1px solid ${C.danger}30`, borderRadius: 5, color: C.danger, padding: "4px 8px", cursor: "pointer", fontSize: 11 }}>✕</button>
          )}
        </div>
      ))}
      {locked && docs.length === 0 && (
        <div style={{ color: C.textDim, fontSize: 12 }}>{t('caseDetail.noDocuments')}</div>
      )}
    </Section>
  );
}

// ─── Assign Panel ─────────────────────────────────────────────────────────────
function AssignPanel({ caseData, team, currentUser, onAssign, locked }) {
  const C = useTheme();
  const [assigneeId, setAssigneeId] = useState(caseData.assigned_to || "");
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  if (!team?.length) return null;

  const assignedMember = team.find(m => m.id === (assigneeId || caseData.assigned_to));

  async function handleAssign(memberId) {
    if (locked) return;
    setAssigneeId(memberId || "");
    setSaving(true);
    await onAssign?.(caseData.id, memberId || null);
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  const { t } = useTranslation();
  return (
    <Section title={t('caseDetail.sectionAssignedTo')} dim={locked} action={
      saving ? <span style={{ color: C.textDim, fontSize: 11 }}>{t('common.loading')}</span>
             : saved  ? <span style={{ color: C.success, fontSize: 11 }}>✓ {t('common.save')}</span>
             : null
    }>
      {locked ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0" }}>
          {assignedMember ? (
            <>
              <div style={{ width: 30, height: 30, borderRadius: "50%", background: `linear-gradient(135deg, ${C.borderLight}, ${C.border})`, display: "flex", alignItems: "center", justifyContent: "center", color: C.textMid, fontWeight: 700, fontSize: 11 }}>
                {assignedMember.first_name?.[0]}{assignedMember.last_name?.[0]}
              </div>
              <div>
                <div style={{ color: C.accent, fontSize: 13 }}>{assignedMember.first_name} {assignedMember.last_name}</div>
                <div style={{ color: C.textDim, fontSize: 11 }}>{assignedMember.role}</div>
              </div>
            </>
          ) : (
            <div style={{ color: C.textDim, fontSize: 13 }}>{t('caseDetail.unassigned')}</div>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <button onClick={() => handleAssign(null)} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 7,
            cursor: "pointer", textAlign: "left",
            background: !assigneeId ? C.accentGlow : C.bg,
            border: `1px solid ${!assigneeId ? C.accent : C.border}`, transition: "all 0.13s",
          }}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: C.border, display: "flex", alignItems: "center", justifyContent: "center", color: C.textDim, fontSize: 14 }}>—</div>
            <div style={{ flex: 1 }}><div style={{ color: C.textMid, fontSize: 13 }}>{t('caseDetail.unassigned')}</div></div>
            {!assigneeId && <span style={{ color: C.accent }}>✓</span>}
          </button>
          {team.map(m => (
            <button key={m.id} onClick={() => handleAssign(m.id)} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 7,
              cursor: "pointer", textAlign: "left",
              background: assigneeId === m.id ? C.accentGlow : C.bg,
              border: `1px solid ${assigneeId === m.id ? C.accent : C.border}`, transition: "all 0.13s",
            }}>
              <div style={{ width: 30, height: 30, borderRadius: "50%", background: `linear-gradient(135deg, ${C.borderLight}, ${C.border})`, display: "flex", alignItems: "center", justifyContent: "center", color: C.textMid, fontWeight: 700, fontSize: 11 }}>
                {m.first_name?.[0]}{m.last_name?.[0]}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: C.text, fontSize: 13 }}>{m.first_name} {m.last_name}</div>
                <div style={{ color: C.textDim, fontSize: 11 }}>{m.role}</div>
              </div>
              {assigneeId === m.id && <span style={{ color: C.accent }}>✓</span>}
            </button>
          ))}
        </div>
      )}
    </Section>
  );
}

// ─── QA Sampling Helper ───────────────────────────────────────────────────────
async function handleQASampling(caseData, company) {
  if (!company?.id || !caseData?.id) return;

  try {
    // Load QA sampling configuration
    const { data: configData } = await db
      .from('qa_sampling_config')
      .select('*')
      .eq('company_id', company.id)
      .limit(1);

    const config = configData?.[0];
    if (!config || !config.enabled) return;

    // Check if case should be sampled (random selection based on percentage)
    const shouldSample = Math.random() * 100 < (config.sampling_percentage || 10);
    if (!shouldSample) return;

    // Check frequency — only route on_close if this is a closed case
    if (config.frequency === 'on_close') {
      // Add to QA queue
      const { error } = await db.from('qa_queue').insert([{
        company_id: company.id,
        case_id: caseData.id,
        entity: caseData.entity,
        type: caseData.type,
        amount: caseData.amount,
        risk: caseData.risk,
        status: config.auto_route ? 'auto_routed' : 'pending',
        sampling_type: 'random',
        created_at: new Date().toISOString(),
      }]);

      if (error) {
        console.warn('[QA] Failed to add case to QA queue:', error);
      } else {
        console.log('[QA] Case added to QA queue:', caseData.id);
      }
    }
  } catch (err) {
    console.error('[QA] Error in QA sampling:', err);
  }
}

// ─── Quick Close/Escalate (no reviewer needed) ────────────────────────────────
function QuickDecisionPanel({ caseData, company, onClosed, onEscalated }) {
  const C = useTheme();
  const [action, setAction] = useState(null);
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const { t } = useTranslation();

  async function handleQuickAction() {
    if (!action || sending) return;
    setSending(true);

    const now = new Date().toISOString();
    const finalStatus = action === "escalate" ? "escalated" : "closed";

    const { error } = await db.from("cases").update({
      status: finalStatus,
      notes: (caseData.notes || "") + (notes ? "\n\n--- FINAL ACTION ---\n" + notes : ""),
      updated_at: now,
    }).eq("id", caseData.id).eq("company_id", company?.id).execute();

    if (error) {
      console.error("Quick action failed:", error.message);
      setSending(false);
      return;
    }

    setSending(false);

    // Route to QA if case was closed and sampling is enabled
    if (action === "close") {
      await handleQASampling(caseData, company);
      onClosed?.({ status: "closed", updated_at: now });
    } else {
      onEscalated?.({ status: "escalated", updated_at: now });
    }
  }

  if (!action) {
    return (
      <Section title={t('caseDetail.sectionDecision')}>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => setAction("close")}
            style={{
              flex: 1, padding: "11px 14px", borderRadius: 7,
              background: `${C.success}18`, border: `1px solid ${C.success}40`,
              color: C.success, fontWeight: 700, cursor: "pointer",
              fontFamily: "'DM Mono', monospace", fontSize: 13,
            }}
          >✓ {t('caseDetail.closeCase')}</button>
          <button
            onClick={() => setAction("escalate")}
            style={{
              flex: 1, padding: "11px 14px", borderRadius: 7,
              background: `${C.danger}18`, border: `1px solid ${C.danger}40`,
              color: C.danger, fontWeight: 700, cursor: "pointer",
              fontFamily: "'DM Mono', monospace", fontSize: 13,
            }}
          >⬆ {t('caseDetail.escalate')}</button>
        </div>
      </Section>
    );
  }

  return (
    <Section title={`${action === "escalate" ? "⬆" : "✓"} ${action === "escalate" ? t('caseDetail.escalate') : t('caseDetail.closeCase')}`}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ color: C.textDim, fontSize: 11, marginBottom: 6 }}>
          {action === "escalate"
            ? t('caseDetail.escalateNotes')
            : t('caseDetail.closeNotes')}
        </div>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder={action === "escalate" ? "Why escalate this case?" : "Why close this case?"}
          rows={3}
          style={{
            width: "100%", background: C.bg, border: `1px solid ${C.border}`,
            borderRadius: 7, color: C.text, fontSize: 13, padding: "9px 12px",
            fontFamily: "'DM Mono', monospace", outline: "none", resize: "vertical",
            boxSizing: "border-box", lineHeight: 1.5,
          }}
        />
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button
          onClick={() => { setAction(null); setNotes(""); }}
          style={{
            flex: 1, padding: "9px", background: "transparent",
            border: `1px solid ${C.border}`, borderRadius: 7,
            color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono', monospace",
            fontSize: 12,
          }}
        >{t('caseDetail.cancel')}</button>
        <button
          onClick={handleQuickAction}
          disabled={sending}
          style={{
            flex: 2, padding: "9px", border: "none", borderRadius: 7,
            background: sending ? C.border : (action === "escalate" ? C.danger : C.success),
            color: sending ? C.textDim : C.bg,
            fontWeight: 700, cursor: sending ? "wait" : "pointer",
            fontFamily: "'DM Mono', monospace", fontSize: 13,
            transition: "all 0.13s",
          }}
        >
          {sending ? t('caseDetail.processing') : (action === "escalate" ? t('caseDetail.escalateNow') : t('caseDetail.closeNow'))}
        </button>
      </div>
    </Section>
  );
}

// ─── Decision Panel (submitter) ───────────────────────────────────────────────
function DecisionPanel({ caseData, team, currentUser, company, onSubmitForReview, onClosed, onEscalated }) {
  const C = useTheme();
  const [action,      setAction]      = useState(null);
  const [reviewerId,  setReviewerId]  = useState("");
  const [notes,       setNotes]       = useState("");
  const [sending,     setSending]     = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  const reviewers = (team || []).filter(m => m.id !== currentUser?.id);
  const canSubmit = !!(action && notes.trim() && reviewerId);
  const missing = [
    ...(!notes.trim()  ? ["add notes"]         : []),
    ...(!reviewerId    ? ["select a reviewer"]  : []),
  ];

  const hasReviewers = reviewers.length > 0;

  async function handleSubmit() {
    if (!canSubmit || sending) return;
    setSending(true);
    setEmailStatus("sending");

    const reviewer = team.find(m => m.id === reviewerId);
    const now = new Date().toISOString();

    // Only update cases table — case_reviews is written by the reviewer on decision
    const { error } = await db.from("cases").update({
      company_id:    company?.id,
      status:        "pending_review",
      review_action: action,
      review_notes:  notes,
      submitted_at:  now,
    }).eq("id", caseData.id).execute();

    if (error) {
      console.error("Case update failed:", error.message);
      setSending(false);
      setEmailStatus("failed");
      return;
    }

    // Email notification (optional, non-blocking)
    try {
      const result = await sendReviewEmail({ reviewer, submittedBy: currentUser, caseData, action, notes });
      setEmailStatus(result.skipped ? "skipped" : "sent");
    } catch (e) {
      setEmailStatus("skipped");
    }

    setSending(false);
    onSubmitForReview?.({
      ...caseData,
      status:        "pending_review",
      review_action: action,
      review_notes:  notes,
      submitted_at:  now,
      reviewer,
    });
    setTimeout(() => setEmailStatus(null), 4000);
  }

  const { t } = useTranslation();

  // Admins can close/escalate without secondary approval
  // Analysts and others need to send for review
  const isAdmin = currentUser?.role === 'admin' || currentUser?.permission === 'admin';

  // If admin or no reviewers available, show quick close/escalate panel
  if (isAdmin || !hasReviewers) {
    return <QuickDecisionPanel caseData={caseData} company={company} onClosed={onClosed} onEscalated={onEscalated} />;
  }

  return (
    <Section title={t('caseDetail.sectionDecision')}>
      {emailStatus && (
        <div style={{
          padding: "9px 12px", borderRadius: 7, marginBottom: 14, fontSize: 12,
          ...(emailStatus === "sent"    ? { background: `${C.success}12`, border: `1px solid ${C.success}40`, color: C.success } :
              emailStatus === "failed"  ? { background: `${C.danger}12`,  border: `1px solid ${C.danger}40`,  color: C.danger  } :
              emailStatus === "sending" ? { background: `${C.accent}10`,  border: `1px solid ${C.accent}30`,  color: C.accent  } :
                                          { background: `${C.border}30`,  border: `1px solid ${C.border}`,    color: C.textDim }),
        }}>
          {emailStatus === "sending" && t('caseDetail.submittingEmail')}
          {emailStatus === "sent"    && t('caseDetail.submittedEmailSent')}
          {emailStatus === "skipped" && t('caseDetail.submittedConfigEmail')}
          {emailStatus === "failed"  && t('caseDetail.submittedEmailFailed')}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        {[
          { id: "escalate", label: t('caseDetail.escalateBtn'),  color: C.danger  },
          { id: "close",    label: t('caseDetail.closeCaseBtn'), color: C.success },
        ].map(opt => (
          <button key={opt.id} onClick={() => setAction(action === opt.id ? null : opt.id)} style={{
            flex: 1, padding: "10px", borderRadius: 7, cursor: "pointer",
            background: action === opt.id ? `${opt.color}18` : C.bg,
            border: `1px solid ${action === opt.id ? opt.color : C.border}`,
            color: action === opt.id ? opt.color : C.textMid,
            fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 600, transition: "all 0.13s",
          }}>{opt.label}</button>
        ))}
      </div>

      {action && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Notes */}
          <div>
            <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", marginBottom: 6 }}>
              {action === "escalate" ? t('caseDetail.escalationNotes') : t('caseDetail.closureNotes')}
              <span style={{ color: C.danger, marginLeft: 4 }}>*</span>
            </div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder={action === "escalate"
                ? t('caseDetail.escalationNotes')
                : t('caseDetail.closureNotes')}
              rows={4}
              style={{
                width: "100%", background: C.bg,
                border: `1px solid ${notes.trim() ? C.borderLight : C.border}`,
                borderRadius: 7, color: C.text, fontSize: 13, padding: "10px 12px",
                fontFamily: "'DM Mono', monospace", outline: "none", resize: "vertical",
                boxSizing: "border-box", lineHeight: 1.6, transition: "border 0.13s",
              }} />
          </div>

          {/* Reviewer — required */}
          <div>
            <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", marginBottom: 6 }}>
              {t('caseDetail.sendToReviewer')} <span style={{ color: C.danger }}>*</span>
            </div>
            {reviewers.length === 0 ? (
              <div style={{ color: C.textDim, fontSize: 12, padding: "10px 12px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7 }}>
                {t('caseDetail.noOtherTeamMembers')}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {reviewers.map(m => (
                  <button key={m.id} onClick={() => setReviewerId(reviewerId === m.id ? "" : m.id)} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                    borderRadius: 7, cursor: "pointer", textAlign: "left",
                    background: reviewerId === m.id ? C.accentGlow : C.bg,
                    border: `1px solid ${reviewerId === m.id ? C.accent : C.border}`, transition: "all 0.13s",
                  }}>
                    <div style={{ width: 30, height: 30, borderRadius: "50%", background: `linear-gradient(135deg, ${C.borderLight}, ${C.border})`, display: "flex", alignItems: "center", justifyContent: "center", color: C.textMid, fontWeight: 700, fontSize: 11, flexShrink: 0 }}>
                      {m.first_name?.[0]}{m.last_name?.[0]}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: C.text, fontSize: 13 }}>{m.first_name} {m.last_name}</div>
                      <div style={{ color: C.textDim, fontSize: 11 }}>{m.role}{m.email && ` · ${m.email}`}</div>
                    </div>
                    {reviewerId === m.id && <span style={{ color: C.accent, fontSize: 14 }}>✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {missing.length > 0 && (
            <div style={{ color: C.textDim, fontSize: 11, padding: "6px 10px", background: `${C.warn}08`, border: `1px solid ${C.warn}20`, borderRadius: 6 }}>
              {t('caseDetail.stillNeeded', { items: missing.join(" and ") })}
            </div>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => { setAction(null); setNotes(""); setReviewerId(""); }} style={{
              flex: 1, padding: "9px", background: "transparent", border: `1px solid ${C.border}`,
              borderRadius: 7, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 12,
            }}>{t('caseDetail.cancel')}</button>
            <button onClick={handleSubmit} disabled={!canSubmit || sending} style={{
              flex: 2, padding: "9px", border: "none", borderRadius: 7,
              background: canSubmit && !sending ? (action === "escalate" ? C.danger : C.success) : C.border,
              color: canSubmit && !sending ? C.bg : C.textDim,
              fontWeight: 700, cursor: canSubmit && !sending ? "pointer" : "not-allowed",
              fontFamily: "'DM Mono', monospace", fontSize: 13, transition: "all 0.13s",
            }}>
              {sending ? t('caseDetail.submitting') : t('caseDetail.submitForReview')}
            </button>
          </div>
        </div>
      )}
    </Section>
  );
}

// ─── Review Panel (shown to the reviewer) ────────────────────────────────────
// Status-driven — no UUID matching needed. Shows to anyone when case is pending_review.
function ReviewActionPanel({ caseData, company, onDecision }) {
  const C = useTheme();
  const [responseNote, setResponseNote] = useState("");
  const [responding,   setResponding]   = useState(false);
  const { toasts, showToast, dismissToast } = useToast();

  async function handleResponse(decision) {
    setResponding(true);
    const now = new Date().toISOString();

    // Insert audit record into case_reviews
    const { error: reviewErr } = await db.from("case_reviews").insert({
      case_id:      caseData.id,
      company_id:   company?.id,
      reviewed_by:  currentUser?.id || null,
      review_notes: responseNote || null,
      status:       decision,
    });
    if (reviewErr) {
      console.error("[review] insert failed:", reviewErr.message);
      showToast("Failed to submit review decision — please try again", "error");
      setResponding(false);
      return;
    }

    if (decision === "approved") {
      const finalStatus = caseData.review_action === "escalate" ? "escalated" : "closed";
      await db.from("cases").update({
        status:     finalStatus,
        closed_at:  now,
      }).eq("id", caseData.id).eq("company_id", company?.id).execute();

      // Route to QA if case was closed and sampling is enabled
      if (finalStatus === "closed") {
        await handleQASampling(caseData, company);
      }

      onDecision?.({ status: finalStatus });
    } else {
      // Send back for rework
      await db.from("cases").update({
        status:        "open",
        submitted_by:  null,
        reviewer_id:   null,
        review_action: null,
        review_notes:  null,
        submitted_at:  null,
      }).eq("id", caseData.id).eq("company_id", company?.id).execute();
      onDecision?.({ status: "open" });
    }
    setResponding(false);
  }

  const { t } = useTranslation();
  return (
    <div style={{ background: `${C.gold}08`, border: `1px solid ${C.gold}40`, borderRadius: 10, padding: 20, marginBottom: 16 }}>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <span style={{ fontSize: 20 }}>🔔</span>
        <div>
          <div style={{ color: C.gold, fontSize: 13, fontWeight: 700 }}>{t('caseDetail.pendingReview')}</div>
          <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>
            {t('caseDetail.submittedFor')}{" "}
            <span style={{ color: caseData.review_action === "escalate" ? C.danger : C.success, fontWeight: 600 }}>
              {caseData.review_action || "review"}
            </span>
            {caseData.submitted_at && ` · ${new Date(caseData.submitted_at).toLocaleString()}`}
          </div>
        </div>
      </div>
      {caseData.review_notes && (
        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, padding: "10px 12px", marginBottom: 14 }}>
          <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 4 }}>{t('caseDetail.submissionNotes')}</div>
          <div style={{ color: C.text, fontSize: 13, lineHeight: 1.6 }}>{caseData.review_notes}</div>
        </div>
      )}
      <div style={{ marginBottom: 12 }}>
        <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", marginBottom: 6 }}>{t('caseDetail.yourNotes')}</div>
        <textarea value={responseNote} onChange={e => setResponseNote(e.target.value)}
          placeholder={t('caseDetail.addComments')}
          rows={3}
          style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: "9px 12px", fontFamily: "'DM Mono', monospace", outline: "none", resize: "vertical", boxSizing: "border-box", lineHeight: 1.5 }} />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => handleResponse("approved")} disabled={responding}
          style={{ flex: 1, padding: "9px", background: `${C.success}18`, border: `1px solid ${C.success}50`, borderRadius: 7, color: C.success, fontWeight: 700, cursor: responding ? "wait" : "pointer", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
          {t('caseDetail.approveClose')}
        </button>
        <button onClick={() => handleResponse("changes_requested")} disabled={responding}
          style={{ flex: 1, padding: "9px", background: `${C.gold}18`, border: `1px solid ${C.gold}50`, borderRadius: 7, color: C.gold, fontWeight: 700, cursor: responding ? "wait" : "pointer", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
          {t('caseDetail.sendBackRework')}
        </button>
      </div>
    </div>
  );
}

// ─── Review History ───────────────────────────────────────────────────────────
function ReviewHistory({ reviews }) {
  const C = useTheme();
  const { t } = useTranslation();
  const done = (reviews || []).filter(r => r.status !== "pending");
  if (!done.length) return null;
  const cfg = {
    approved:          { color: C.success, label: t('caseDetail.reviewApproved'),          icon: "✓" },
    denied:            { color: C.danger,  label: t('caseDetail.reviewDenied'),            icon: "✕" },
    changes_requested: { color: C.gold,    label: t('caseDetail.reviewChangesRequested'),  icon: "✎" },
  };
  return (
    <Section title={t('caseDetail.sectionReviewHistory')}>
      {done.map((r, i) => {
        const c = cfg[r.status] || { color: C.textDim, label: r.status, icon: "·" };
        const reviewer = r.reviewer_profile;
        return (
          <div key={r.id || i} style={{ padding: "10px 0", borderBottom: i < done.length - 1 ? `1px solid ${C.border}` : "none" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{ color: c.color, fontSize: 13, fontWeight: 700 }}>{c.icon} {c.label}</span>
              {reviewer && <span style={{ color: C.textDim, fontSize: 11 }}>{t('caseDetail.reviewedBy')} {reviewer.first_name} {reviewer.last_name}</span>}
              <span style={{ color: C.textDim, fontSize: 11, marginLeft: "auto" }}>
                {r.reviewed_at ? new Date(r.reviewed_at).toLocaleString() : ""}
              </span>
            </div>
            {r.reviewer_note && <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.5 }}>{r.reviewer_note}</div>}
          </div>
        );
      })}
    </Section>
  );
}

// ─── Main CaseDetail Component ────────────────────────────────────────────────
export default function CaseDetail({
  caseData: initialCaseData,
  onBack,
  onCaseUpdated,
  team: rawTeam = [],
  currentUser: rawCurrentUser,
  onTransactionClick,
  company,
}) {
  const C = useTheme();
  const { t } = useTranslation();
  // Normalize camelCase (App.jsx) vs snake_case (Supabase) field names
  const team        = rawTeam.map(norm);
  const currentUser = norm(rawCurrentUser);
  const { log } = useAuditLog(company, rawCurrentUser);

  const [caseData,        setCaseData]        = useState(initialCaseData);
  const [reviews,         setReviews]         = useState([]);
  const [loadingReviews,  setLoadingReviews]  = useState(true);
  const [transactions,    setTransactions]    = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [showAllTx,       setShowAllTx]       = useState(false);
  const [loadingTx,       setLoadingTx]       = useState(true);
  const [loadingAllTx,    setLoadingAllTx]    = useState(false);
  // Track docs at CaseDetail level so exportCasePDF can include them
  const [caseDocsMeta,    setCaseDocsMeta]    = useState([]);

  // ── Investigation Analysis (5W Framework) ────────────────────────────────────
  const [inv, setInv] = useState({
    subjectType: "", subjectRole: "", otherParties: [],
    activityType: [], redFlags: [], paymentMethods: [],
    jurisdictions: [], accountTypes: [],
    timingPattern: "", frequency: "", duration: "",
    suspectedMotive: [], suspectedPurpose: "",
  });
  const [invOpen, setInvOpen] = useState(false);

  function toggleInv(field, value) {
    setInv(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value],
    }));
  }

  function buildNarrativeDraft() {
    const today = new Date().toISOString().slice(0, 10);
    const lines = [];
    lines.push(`INVESTIGATION ANALYSIS — ${caseData.id} — ${today}`);
    lines.push(`Entity: ${caseData.entity} | Alert Type: ${caseData.type} | Risk: ${caseData.risk}`);
    lines.push("");

    lines.push("─── WHO ───────────────────────────────────────────");
    if (inv.subjectType) lines.push(`Subject Type: ${inv.subjectType}`);
    if (inv.subjectRole) lines.push(`Subject Role: ${inv.subjectRole}`);
    if (inv.otherParties.length) lines.push(`Other Parties Involved: ${inv.otherParties.join(", ")}`);
    lines.push("");

    lines.push("─── WHAT ──────────────────────────────────────────");
    if (inv.activityType.length) lines.push(`Activity Type: ${inv.activityType.join(", ")}`);
    if (inv.redFlags.length) lines.push(`Red Flags Observed: ${inv.redFlags.join("; ")}`);
    if (inv.paymentMethods.length) lines.push(`Payment Methods: ${inv.paymentMethods.join(", ")}`);
    lines.push("");

    lines.push("─── WHERE ─────────────────────────────────────────");
    if (inv.jurisdictions.length) lines.push(`Jurisdictions: ${inv.jurisdictions.join(", ")}`);
    if (inv.accountTypes.length) lines.push(`Account Types: ${inv.accountTypes.join(", ")}`);
    lines.push("");

    lines.push("─── WHEN ──────────────────────────────────────────");
    if (inv.timingPattern) lines.push(`Timing Pattern: ${inv.timingPattern}`);
    if (inv.frequency) lines.push(`Transaction Frequency: ${inv.frequency}`);
    if (inv.duration) lines.push(`Duration of Concern: ${inv.duration}`);
    lines.push("");

    lines.push("─── WHY ───────────────────────────────────────────");
    if (inv.suspectedMotive.length) lines.push(`Suspected Motive(s): ${inv.suspectedMotive.join(", ")}`);
    if (inv.suspectedPurpose) lines.push(`Suspected Purpose: ${inv.suspectedPurpose}`);
    lines.push("");

    lines.push("─── NARRATIVE ─────────────────────────────────────");
    lines.push("[Continue narrative here based on the above analysis...]");

    const draft = lines.join("\n");
    setNarrative(prev => prev ? `${prev}\n\n${draft}` : draft);
  }

  // ── Case Narrative ───────────────────────────────────────────────────────────
  const narrativeKey = `crg_narrative_${initialCaseData.id}`;
  // Load from Supabase first, fall back to localStorage for existing data
  const [narrative, setNarrative] = useState(() => {
    try { return initialCaseData.narrative || localStorage.getItem(narrativeKey) || ""; } catch { return ""; }
  });
  const [narrativeSaved, setNarrativeSaved] = useState(false);

  async function saveNarrative() {
    // Log audit trail
    log({
      action: "case_narrative_updated",
      module: "cases",
      entityId: String(caseData.id),
      entityName: caseData.entity,
      meta: { narrative_length: narrative?.length || 0 },
    });

    // Persist to Supabase + localStorage fallback
    await db.from("cases").update({ narrative }).eq("id", caseData.id).execute();
    try { localStorage.setItem(narrativeKey, narrative); } catch {}
    setNarrativeSaved(true);
    setTimeout(() => setNarrativeSaved(false), 2000);
  }

  // ── AI Guidance ──────────────────────────────────────────────────────────────
  // Load saved AI chat from Supabase (ai_chat column on cases table)
  const [aiMessages, setAiMessages] = useState(() => {
    try { return Array.isArray(initialCaseData.ai_chat) ? initialCaseData.ai_chat : []; } catch { return []; }
  });
  const [aiInput,    setAiInput]    = useState("");
  const [aiLoading,  setAiLoading]  = useState(false);
  const aiEndRef = useRef(null);

  useEffect(() => { aiEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [aiMessages]);

  async function persistAiMessages(msgs) {
    await db.from("cases").update({ ai_chat: msgs }).eq("id", caseData.id).execute();
  }

  async function sendAiMessage() {
    if (!aiInput.trim() || aiLoading) return;
    const userMsg = { role: "user", content: aiInput };
    setAiMessages(prev => [...prev, userMsg]);
    setAiInput("");
    setAiLoading(true);
    try {
      const system = `You are an AML/CFT compliance analyst AI assistant reviewing this case:

Case ID: ${caseData.id}
Entity: ${caseData.entity}
Alert Type: ${caseData.type}
Risk Level: ${caseData.risk}
Amount: ${caseData.amount}
Country: ${caseData.country}
Date: ${caseData.date}
Status: ${caseData.status}
Notes: ${caseData.notes || "None"}${narrative ? `

Case narrative so far:
${narrative}` : ""}

Provide expert AML compliance guidance. Be specific and actionable. Reference FATF, FinCEN, FCA, or JMLSG where relevant.`;

      const reply = await callAI({ system, messages: [...aiMessages, userMsg], maxTokens: 1000 });
      const updated = [...aiMessages, userMsg, { role: "assistant", content: reply }];
      setAiMessages(updated);
      persistAiMessages(updated);
    } catch (e) {
      const updated = [...aiMessages, userMsg, { role: "assistant", content: `⚠ ${e.message}` }];
      setAiMessages(updated);
    } finally {
      setAiLoading(false);
    }
  }

  const riskColor   = { HIGH: C.danger, MEDIUM: C.warn, LOW: C.success };
  const statusColor = {
    open:           C.warn,
    pending_review: C.gold,
    under_review:   C.gold,
    escalated:      C.danger,
    closed:         C.textDim,
    merged:         C.textDim,
    new:            C.gold,
  };
  const statusLabel = {
    open:           t('caseDetail.statusLabels.open'),
    pending_review: t('caseDetail.statusLabels.pending_review'),
    under_review:   t('caseDetail.statusLabels.under_review'),
    escalated:      t('caseDetail.statusLabels.escalated'),
    closed:         t('caseDetail.statusLabels.closed'),
    merged:         t('caseDetail.statusLabels.merged'),
    new:            t('caseDetail.statusLabels.new'),
  };

  // Load reviews — no broken joins, enrich client-side
  async function loadReviews() {
    setLoadingReviews(true);
    try {
      const result = await db
        .from("case_reviews")
        .select("*")
        .eq("case_id", caseData.id)
        .order("created_at", { ascending: true }).execute();
      const enrich = id => team.find(m => m.id === id || m.user_id === id) || null;
      const enriched = (result?.data || []).map(r => ({
        ...r,
        reviewer_profile:     enrich(r.reviewer_id),
        submitted_by_profile: enrich(r.submitted_by),
      }));
      setReviews(enriched);
    } catch (e) {
      console.error('[CaseDetail] Error loading reviews:', e);
      setReviews([]);
    }
    setLoadingReviews(false);
  }

  // Load latest case data — simple select, enrich client-side
  async function loadCase() {
    try {
      const result = await db
        .from("cases")
        .select("*")
        .eq("id", caseData.id)
        .single()
        .execute();
      if (result?.data && Array.isArray(result.data) && result.data.length > 0) {
        const data = result.data[0];
        const enrich = id => team.find(m => m.id === id || m.user_id === id) || null;
        setCaseData({
          ...data,
          assignee_profile:     enrich(data.assigned_to),
          reviewer_profile:     enrich(data.reviewer_id),
          submitted_by_profile: enrich(data.submitted_by),
          closed_by_profile:    enrich(data.closed_by),
        });
      }
    } catch (e) {
      console.error('[CaseDetail] Error loading case:', e);
    }
  }

  // Load real transactions for this entity
  async function loadTransactions() {
    setLoadingTx(true);
    if (!company?.id) { setLoadingTx(false); return; }
    try {
      const queries = [];

      // If case has a trigger_transaction_ref, load that specific transaction
      if (caseData.trigger_transaction_ref) {
        queries.push(
          db.from("transactions").select("*")
            .eq("company_id", company.id)
            .eq("tx_reference", caseData.trigger_transaction_ref)
            .execute()
        );
      } else {
        // Fallback: search by entity name, limited to tx_count
        if (caseData.entity && caseData.entity.toLowerCase() !== 'unknown') {
          queries.push(
            db.from("transactions").select("*")
              .eq("company_id", company.id)
              .ilike("entity", caseData.entity)
              .order("created_at", { ascending: false })
              .limit(caseData.tx_count || 1)
              .execute()
          );
        } else if (caseData.entity?.toLowerCase() === 'unknown') {
          // For unknown entities, search by entity field matching "unknown" or empty/null
          queries.push(
            db.from("transactions").select("*")
              .eq("company_id", company.id)
              .or("entity.ilike.unknown,entity.is.null")
              .order("created_at", { ascending: false })
              .limit(caseData.tx_count || 1)
              .execute()
          );
        }
      }

      const results = await Promise.allSettled(queries);
      const seen = new Set(); const merged = [];
      for (const result of results) {
        if (result.status === 'fulfilled') {
          const data = result.value?.data || [];
          if (Array.isArray(data)) {
            for (const row of data) {
              if (row?.id && !seen.has(row.id)) { seen.add(row.id); merged.push(row); }
            }
          }
        }
      }
      merged.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      setTransactions(merged);
    } catch (e) {
      console.error('[CaseDetail] Error loading transactions:', e);
      setTransactions([]);
    }
    setLoadingTx(false);
  }

  async function loadAllTransactions() {
    if (!company?.id || !caseData.entity) return;
    setLoadingAllTx(true);
    try {
      let query = db.from("transactions").select("*")
        .eq("company_id", company.id);

      if (caseData.entity.toLowerCase() === 'unknown') {
        query = query.or("entity.ilike.unknown,entity.is.null");
      } else {
        query = query.ilike("entity", caseData.entity);
      }

      const result = await query
        .order("created_at", { ascending: false })
        .limit(5000).execute();
      setAllTransactions(Array.isArray(result?.data) ? result.data : []);
    } catch (e) {
      console.error('[CaseDetail] Error loading all transactions:', e);
      setAllTransactions([]);
    }
    setLoadingAllTx(false);
  }

  useEffect(() => {
    loadReviews();
    loadCase();
    loadTransactions();
  }, [caseData.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // TODO: Implement real-time updates via WebSockets or polling
  // Real-time subscriptions via Supabase .channel() are not currently supported
  // Case updates are not reflected until page reload
  useEffect(() => {
    // Real-time subscriptions disabled - implement polling if real-time updates are needed
    return () => {};
  }, [caseData.id]);

  // Derived state
  const isComplete      = caseData.status === "closed" || caseData.status === "escalated" || caseData.status === "merged";
  const isPendingReview = caseData.status === "pending_review";
  const isUnderReview   = caseData.status === "under_review";
  const isLockedForMe   = false; // ReviewActionPanel handles flow for anyone who opens case
  const pendingReview   = reviews.find(r => r.status === "pending");
  const assignedMember  = caseData.assignee_profile || team.find(m => m.id === caseData.assigned_to);
  const closedByMember  = caseData.closed_by_profile;
  const reviewerMember  = caseData.reviewer_profile || team.find(m => m.id === caseData.reviewer_id);

  async function handleAssign(caseId, memberId) {
    // Log audit trail
    log({
      action: "case_assigned",
      module: "cases",
      entityId: String(caseId),
      entityName: caseData.entity,
      newValue: { assigned_to: memberId },
      meta: { assigned_member_id: memberId },
    });

    // Update the case row
    await db.from("cases").update({
      assigned_to: memberId,
    }).eq("id", caseId).eq("company_id", company?.id).execute();
    setCaseData(prev => ({ ...prev, assigned_to: memberId }));
    onCaseUpdated?.();

    if (!memberId || !company?.id) return;

    const assignee = team.find(m => m.id === memberId);
    if (!assignee) return;

    // Write to unified assignments table
    try {
      await db.from("assignments").insert({
        company_id:  company.id,
        assigned_by: currentUser?.id || null,
        assigned_to: memberId,
        type:        "case",
        ref_id:      caseId,
        ref_title:   caseData.entity || caseId,
        status:      "pending",
      });
    } catch (e) {
      console.warn("[assign] assignments insert failed:", e.message);
    }

    // Send in-app notification
    try {
      await db.from("notifications").insert({
        company_id:  company.id,
        user_id:     memberId,
        type:        "alert_assigned",
        title:       `Case assigned: ${caseData.entity || caseId}`,
        body:        `You have been assigned case ${caseId}. Open Case Management to review it.`,
        link_module: "cases",
        link_id:     caseId,
      });
    } catch (e) {
      console.warn("[assign] notification insert failed:", e.message);
    }
  }

  function handleSubmitForReview(review) {
    log({
      action: "case_submitted_for_review",
      module: "cases",
      entityId: String(caseData.id),
      entityName: caseData.entity,
      meta: { action: review.review_action, submitted_at: review.submitted_at },
    });
    const updated = {
      ...caseData,
      status:        "pending_review",
      submitted_by:  currentUser?.id,
      reviewer_id:   review.reviewer?.id,
      review_action: review.action,
      review_notes:  review.notes,
      submitted_at:  review.submitted_at,
    };
    setCaseData(updated);
    loadReviews();
    onCaseUpdated?.(updated);
    // Return submitter to the list so they immediately see it locked
    setTimeout(() => onBack?.(), 1200);
  }

  async function handleReviewDecision({ status, reviewerNote, closedBy, denied }) {
    log({
      action: denied ? "case_review_denied" : "case_review_approved",
      module: "cases",
      entityId: String(caseData.id),
      entityName: caseData.entity,
      newValue: { status },
      meta: { reviewer_note: reviewerNote || null, closed_by: closedBy?.id || null },
    });
    // If approved/closed, mark assignments as done
    if (!denied && company?.id) {
      const { error } = await db.from("assignments")
        .update({ status: "done" })
        .eq("company_id", company.id)
        .eq("type", "case")
        .eq("ref_id", String(caseData.id))
        .execute();
      if (error) console.warn("[assignment] close failed:", error.message);
    }
    setCaseData(prev => {
      const updated = {
        ...prev,
        status,
        closed_by:    closedBy?.id,
        closed_at:    denied ? null : new Date().toISOString(),
        submitted_by: denied ? null : prev.submitted_by,
        reviewer_id:  denied ? null : prev.reviewer_id,
      };
      onCaseUpdated?.(updated);
      return updated;
    });
    loadReviews();
    // Return reviewer to list after approving/denying so they see updated status
    setTimeout(() => onBack?.(), 1200);
  }

  async function handleMarkUnderReview() {
    const newStatus = caseData.status === "pending_review" ? "open" : "pending_review";

    // Log audit trail
    log({
      action: `case_status_changed_to_${newStatus}`,
      module: "cases",
      entityId: String(caseData.id),
      entityName: caseData.entity,
      newValue: { status: newStatus },
      oldValue: { status: caseData.status },
    });

    const { error } = await db.from("cases").update({
      status: newStatus,
    }).eq("id", caseData.id).eq("company_id", company?.id).execute();

    if (!error) {
      setCaseData(prev => ({ ...prev, status: newStatus }));
      onCaseUpdated?.();
    } else {
      console.error("Failed to update case status:", error);
    }
  }

  function exportCasePDF() {
    const aiConvo = aiMessages.map(m => `<tr><td style="font-weight:600;white-space:nowrap;color:${m.role==="user"?"#0A2342":"#2D7D46"}">${m.role==="user"?"Analyst":"AI"}</td><td>${String(m.content).replace(/</g,"&lt;").replace(/\n/g,"<br>")}</td></tr>`).join("");
    const reviewRows = reviews.map(r => `<tr><td>${new Date(r.created_at||r.submitted_at||"").toLocaleDateString()}</td><td>${r.action||r.status||""}</td><td>${(r.submitted_by_profile?.first_name||"")} ${(r.submitted_by_profile?.last_name||"")}</td><td>${r.notes||r.review_notes||"—"}</td></tr>`).join("");
    const html = `<div class="section"><div class="section-title">CASE OVERVIEW</div><table><tbody>
      <tr><td><b>Case ID</b></td><td>${caseData.id}</td><td><b>Entity</b></td><td>${caseData.entity}</td></tr>
      <tr><td><b>Status</b></td><td>${(caseData.status||"").toUpperCase()}</td><td><b>Risk</b></td><td>${caseData.risk||"—"}</td></tr>
      <tr><td><b>Amount</b></td><td>${caseData.amount||"—"}</td><td><b>Type</b></td><td>${caseData.type||"—"}</td></tr>
      <tr><td><b>Date</b></td><td>${caseData.date||"—"}</td><td><b>Country</b></td><td>${caseData.country||"—"}</td></tr>
      <tr><td><b>Assigned</b></td><td colspan="3">${assignedMember?`${assignedMember.first_name} ${assignedMember.last_name}`:"Unassigned"}</td></tr>
      ${caseData.notes?`<tr><td><b>Notes</b></td><td colspan="3">${caseData.notes}</td></tr>`:""}
    </tbody></table></div>
    ${narrative?`<div class="section"><div class="section-title">CASE NARRATIVE</div><div style="font-size:11px;line-height:1.7;white-space:pre-wrap">${narrative.replace(/</g,"&lt;")}</div></div>`:""}
    ${aiMessages.length?`<div class="section"><div class="section-title">AI ANALYSIS</div><table><tbody>${aiConvo}</tbody></table></div>`:""}
    ${reviews.length?`<div class="section"><div class="section-title">REVIEW HISTORY</div><table><thead><tr><th>Date</th><th>Action</th><th>By</th><th>Notes</th></tr></thead><tbody>${reviewRows}</tbody></table></div>`:""}
    ${caseDocsMeta.length?`<div class="section"><div class="section-title">ATTACHED DOCUMENTS (${caseDocsMeta.length})</div><table><thead><tr><th>File Name</th><th>Size</th><th>Uploaded</th><th>By</th></tr></thead><tbody>${
      caseDocsMeta.map(d=>`<tr><td>${d.name}</td><td>${d.size?(d.size/1024).toFixed(1)+' KB':'—'}</td><td>${d.created_at?new Date(d.created_at).toLocaleDateString():'—'}</td><td>${d.uploaded_by||'—'}</td></tr>`).join('')
    }</tbody></table></div>`:""}
    ${transactions.length?`<div class="section"><div class="section-title">LINKED TRANSACTIONS (${transactions.length})</div><table><thead><tr><th>Date</th><th>Amount</th><th>Type</th><th>Status</th><th>Counterparty</th></tr></thead><tbody>${
      transactions.slice(0,25).map(tx=>`<tr><td>${tx.date||'—'}</td><td>${tx.amount||'—'} ${tx.currency||''}</td><td>${tx.type||'—'}</td><td>${tx.status||'—'}</td><td>${tx.counterparty||tx.entity||'—'}</td></tr>`).join('')
    }${transactions.length>25?`<tr><td colspan="5" style="color:#4A5568;font-style:italic">… ${transactions.length-25} more transactions — export CSV for full list</td></tr>`:''}</tbody></table></div>`:""}`;
    const w = window.open("","_blank");
    if(!w) return;
    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Case ${caseData.id}</title><style>
    @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Syne:wght@700;800&display=swap');
    *{box-sizing:border-box;margin:0;padding:0}body{font-family:'DM Mono',monospace;background:#fff;color:#0A2342;padding:12mm;font-size:11px}
    .header{display:flex;align-items:center;gap:10px;margin-bottom:6mm;padding-bottom:4mm;border-bottom:3px solid #00D4FF}
    .logo-hex{width:26px;height:26px;background:#00D4FF;clip-path:polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%);flex-shrink:0}
    .logo-text{font-family:'Syne',sans-serif;font-weight:800;font-size:16px;color:#0A2342}
    .doc-title{font-family:'Syne',sans-serif;font-size:18px;font-weight:700;margin-bottom:1mm}
    .doc-meta{font-size:9px;color:#4A5568;margin-bottom:5mm}
    .section{margin-bottom:5mm}.section-title{font-family:'Syne',sans-serif;font-weight:700;font-size:11px;letter-spacing:0.1em;margin-bottom:2mm;padding-bottom:1mm;border-bottom:1px solid #E2E8F0}
    table{width:100%;border-collapse:collapse;font-size:10px}th{background:#0A2342;color:#fff;padding:5px 7px;text-align:left;font-size:9px}
    td{padding:4px 7px;border-bottom:1px solid #E2E8F0;vertical-align:top}tr:nth-child(even) td{background:#F7FAFC}
    .footer{margin-top:6mm;padding-top:2mm;border-top:1px solid #E2E8F0;font-size:8px;color:#8A9BB5;display:flex;justify-content:space-between}
    @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
    </style></head><body>
    <div class="header"><div class="logo-hex"></div><span class="logo-text">CrossRegGuard</span></div>
    <div class="doc-title">Case Report — ${caseData.entity}</div>
    <div class="doc-meta">Case ID: ${caseData.id} · Generated: ${new Date().toLocaleString()}</div>
    ${html}
    <div class="footer"><span>CrossRegGuard — Confidential</span><span>Case ${caseData.id} · ${new Date().toLocaleDateString()}</span></div>
    </body></html>`);
    w.document.close(); setTimeout(()=>w.print(),800);
  }


  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: C.bg, overflowY: "auto" }}>
      {/* ── Header ── */}
      <div style={{ padding: "20px 28px 16px", borderBottom: `1px solid ${C.border}`, background: C.surface, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button onClick={onBack} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMid, padding: "6px 13px", cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono', monospace" }}>{t('caseDetail.back')}</button>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ color: C.accent, fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{caseData.id}</span>
              <Badge color={riskColor[caseData.risk] || C.textDim}>{caseData.risk} RISK</Badge>
              <Badge color={statusColor[caseData.status] || C.textDim}>{statusLabel[caseData.status] || caseData.status?.toUpperCase()}</Badge>
              {assignedMember && (
                <Badge color={C.accent}>👤 {assignedMember.first_name} {assignedMember.last_name}</Badge>
              )}
              {isPendingReview && reviewerMember && (
                <Badge color={C.gold}>🔍 {t('caseDetail.reviewing')}: {reviewerMember.first_name} {reviewerMember.last_name}</Badge>
              )}
            </div>
            <div style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 700, marginTop: 4 }}>{caseData.entity}</div>
          </div>
          <div style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            <div style={{ color: C.text, fontFamily: "'DM Mono', monospace", fontSize: 18, fontWeight: 700 }}>{caseData.amount}</div>
            <div style={{ color: C.textDim, fontSize: 11 }}>{caseData.type}</div>
            <div style={{ display: "flex", gap: 8 }}>
              {!isComplete && !isPendingReview && caseData.status !== "under_review" && (
                <button onClick={handleMarkUnderReview} style={{ background: `${C.gold}18`, border: `1px solid ${C.gold}50`, borderRadius: 6, color: C.gold, padding: "4px 12px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace", fontWeight: 700 }}>
                  {t('caseDetail.underReview')}
                </button>
              )}
              {caseData.status === "under_review" && (
                <button onClick={handleMarkUnderReview} style={{ background: `${C.gold}30`, border: `1px solid ${C.gold}`, borderRadius: 6, color: C.gold, padding: "4px 12px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace", fontWeight: 700 }}>
                  {t('caseDetail.underReviewActive')}
                </button>
              )}
              <button onClick={exportCasePDF} style={{ background: "transparent", border: `1px solid ${C.accent}`, borderRadius: 6, color: C.accent, padding: "4px 12px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace" }}>{t('caseDetail.exportPDF')}</button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, padding: "20px 28px", display: "grid", gridTemplateColumns: "1fr 340px", gap: 16, minHeight: 0 }}>
        {/* Left column */}
        <div>
          {/* Complete banner */}
          {isComplete && (
            <CompleteBanner caseData={caseData} closedBy={closedByMember} />
          )}

          {/* Locked banner (submitter waiting) */}
          {isLockedForMe && (
            <LockedBanner review={pendingReview} reviewer={reviewerMember} />
          )}

          {/* Reviewer action panel */}
          {/* Review action panel — shown to anyone when case is pending review */}
          {isPendingReview && (
            <ReviewActionPanel
              caseData={caseData}
              company={company}
              onDecision={handleReviewDecision}
            />
          )}

          {/* Deadline Tracking */}
          <DeadlineDisplay
            alertGeneratedAt={caseData.alert_generated_at || caseData.created_at}
            regulatoryDueDate={caseData.regulatory_due_date}
            internalSLADueDate={caseData.internal_sla_due_date}
          />

          {/* Case details */}
          <Section title={t('caseDetail.sectionCaseDetails')}>
            {[
              [t('caseDetail.alertId'),     caseData.id],
              [t('caseDetail.entity'),      caseData.entity],
              [t('caseDetail.alertType'),   caseData.type],
              [t('caseDetail.country'),     caseData.country],
              [t('caseDetail.date'),        caseData.date],
              [t('caseDetail.transactions'), t('caseDetail.txCount', { count: caseData.tx_count || 1 })],
              [t('caseDetail.assignedTo'),  assignedMember ? `${assignedMember.first_name} ${assignedMember.last_name}` : "—"],
              [t('common.status'),          statusLabel[caseData.status] || caseData.status],
              [t('caseDetail.notes'),       caseData.notes || "—"],
            ].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>
                <span style={{ color: C.textMid }}>{k}</span>
                <span style={{ color: C.text, fontFamily: "'DM Mono', monospace", fontSize: 12, maxWidth: "60%", textAlign: "right" }}>{v}</span>
              </div>
            ))}
          </Section>

          {/* Transactions — Compare All + Export CSV */}
          {(() => {
            const displayTx = showAllTx ? allTransactions : transactions;
            const sectionTitle = showAllTx
              ? t('caseDetail.allTxFor', { entity: (caseData.entity || "").toUpperCase(), count: allTransactions.length })
              : transactions.length
                ? t('caseDetail.txSectionCount', { count: transactions.length })
                : t('caseDetail.txSection');

            function exportTxCSV() {
              if (!displayTx.length) return;
              const keys = ["tx_reference","tx_date","entity_name","amount","currency","tx_type","direction","counterparty_country"];
              const csv = [keys.join(","), ...displayTx.map(tx => keys.map(k => {
                const v = tx[k] == null ? "" : String(tx[k]);
                return v.includes(",") || v.includes('"') ? '"' + v.replace(/"/g, '""') + '"' : v;
              }).join(","))].join("\n");
              const a = document.createElement("a");
              a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
              a.download = "transactions_" + (caseData.entity || "case").replace(/\s+/g, "_") + "_" + new Date().toISOString().slice(0,10) + ".csv";
              a.click();
            }

            return (
              <Section title={sectionTitle} action={
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    onClick={() => { if (!showAllTx && allTransactions.length === 0) loadAllTransactions(); setShowAllTx(v => !v); }}
                    disabled={loadingAllTx}
                    style={{ padding: "4px 10px", borderRadius: 5, cursor: loadingAllTx ? "wait" : "pointer", fontSize: 10, fontFamily: "'DM Mono', monospace", fontWeight: 700, background: showAllTx ? `${C.accent}18` : C.accentGlow, border: `1px solid ${showAllTx ? C.accent : C.borderLight}`, color: C.accent, opacity: loadingAllTx ? 0.6 : 1 }}>
                    {loadingAllTx ? "Loading…" : showAllTx ? t('caseDetail.alertTxsOnly') : t('caseDetail.compareAllTx')}
                  </button>
                  {displayTx.length > 0 && (
                    <button onClick={exportTxCSV} style={{ padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 10, fontFamily: "'DM Mono', monospace", background: "transparent", border: `1px solid ${C.border}`, color: C.textMid }}>
                      {t('caseDetail.exportCSV')}
                    </button>
                  )}
                </div>
              }>
                {(loadingTx || (showAllTx && loadingAllTx)) ? (
                  <div style={{ color: C.textDim, fontSize: 12, padding: "12px 0" }}>{t('caseDetail.loadingTx')}</div>
                ) : displayTx.length === 0 ? (
                  <div style={{ color: C.textDim, fontSize: 12, padding: "12px 0" }}>
                    {t('caseDetail.noTxFound', { entity: caseData.entity })}
                    <div style={{ marginTop: 6, fontSize: 11 }}>{t('caseDetail.noTxHint')}</div>
                  </div>
                ) : (
                  <>
                    {showAllTx && (
                      <div style={{ padding: "6px 10px", marginBottom: 10, background: `${C.accent}08`, border: `1px solid ${C.accent}25`, borderRadius: 6, fontSize: 11, color: C.accent }}>
                        {t('caseDetail.showingAllTx', { count: allTransactions.length, entity: caseData.entity })}
                      </div>
                    )}
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                          {[
                            t('caseDetail.txHeaders.reference'),
                            t('caseDetail.txHeaders.date'),
                            "Primary Party",
                            "Counterparty",
                            t('caseDetail.txHeaders.amount'),
                            t('caseDetail.txHeaders.currency'),
                            t('caseDetail.txHeaders.type'),
                            t('caseDetail.txHeaders.direction'),
                            t('caseDetail.txHeaders.country'),
                          ].map(h => (
                            <th key={h} style={{ textAlign: "left", padding: "6px 8px", color: C.textDim, fontSize: 10, letterSpacing: "0.1em", fontFamily: "'DM Mono', monospace" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {displayTx.map(tx => {
                          const dirColor = tx.direction === "inbound" ? C.success : C.danger;
                          const isAlerted = transactions.some(txItem => txItem.id === tx.id);
                          return (
                            <tr key={tx.id} onClick={() => onTransactionClick?.(tx, caseData)}
                              style={{ borderBottom: `1px solid ${C.border}`, cursor: onTransactionClick ? "pointer" : "default", background: showAllTx && isAlerted ? `${C.warn}08` : "transparent" }}
                              onMouseEnter={e => e.currentTarget.style.background = C.surfaceAlt}
                              onMouseLeave={e => { e.currentTarget.style.background = showAllTx && isAlerted ? `${C.warn}08` : "transparent"; }}>
                              <td style={{ padding: "9px 8px", color: C.accent, fontFamily: "'DM Mono', monospace", fontSize: 11 }}>
                                {tx.tx_reference}
                                {showAllTx && isAlerted && <span style={{ marginLeft: 5, fontSize: 9, color: C.warn, background: `${C.warn}15`, padding: "1px 5px", borderRadius: 3 }}>{t('caseDetail.alerted')}</span>}
                              </td>
                              <td style={{ padding: "9px 8px", color: C.textDim, fontSize: 12 }}>{tx.tx_date?.slice(0, 10)}</td>
                              <td style={{ padding: "9px 8px", color: C.text, fontSize: 12 }}>{tx.entity || "—"}</td>
                              <td style={{ padding: "9px 8px", color: C.text, fontSize: 12 }}>{tx.counterparty_name || tx.counterparty || "—"}</td>
                              <td style={{ padding: "9px 8px", color: C.text, fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 600 }}>{Number(tx.amount).toLocaleString()}</td>
                              <td style={{ padding: "9px 8px", color: C.textMid, fontSize: 12 }}>{tx.currency}</td>
                              <td style={{ padding: "9px 8px", color: C.textMid, fontSize: 12 }}>{tx.tx_type}</td>
                              <td style={{ padding: "9px 8px" }}>
                                <span style={{ color: dirColor, fontSize: 11, fontWeight: 700 }}>{tx.direction === "inbound" ? "↓ IN" : "↑ OUT"}</span>
                              </td>
                              <td style={{ padding: "9px 8px", color: C.textMid, fontSize: 12 }}>{tx.counterparty_country || "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </>
                )}
              </Section>
            );
          })()}

          {/* Review history */}
          {!loadingReviews && <ReviewHistory reviews={reviews} />}

          {/* ── Investigation Analysis (5W) ── */}
          <Section
            title="Investigation Analysis — 5W Framework"
            action={
              <button
                onClick={() => setInvOpen(v => !v)}
                style={{ padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 10, fontFamily: "'DM Mono', monospace", fontWeight: 700, background: invOpen ? `${C.accent}18` : C.accentGlow, border: `1px solid ${invOpen ? C.accent : C.borderLight}`, color: C.accent }}
              >{invOpen ? "▲ Collapse" : "▼ Expand"}</button>
            }
          >
            {invOpen ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

                {/* WHO */}
                <div>
                  <div style={{ color: C.accent, fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 10 }}>WHO — Subject &amp; Parties</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <span style={{ color: C.textDim, fontSize: 11 }}>Subject Type</span>
                      <select value={inv.subjectType} onChange={e => setInv(p => ({ ...p, subjectType: e.target.value }))} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, padding: "7px 10px", fontFamily: "'DM Mono', monospace" }}>
                        <option value="">— Select —</option>
                        {["Individual","Corporate Entity","Shell Company","Trust / Foundation","PEP / Government Official","NGO / Charity","Financial Institution","VASP / Crypto Exchange","Unknown"].map(o => <option key={o}>{o}</option>)}
                      </select>
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <span style={{ color: C.textDim, fontSize: 11 }}>Subject Role</span>
                      <select value={inv.subjectRole} onChange={e => setInv(p => ({ ...p, subjectRole: e.target.value }))} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, padding: "7px 10px", fontFamily: "'DM Mono', monospace" }}>
                        <option value="">— Select —</option>
                        {["Account Holder","Beneficial Owner (UBO)","Originator","Beneficiary","Authorized Signatory","Director / Officer","Intermediary / Agent","Third-Party Payor","Unknown"].map(o => <option key={o}>{o}</option>)}
                      </select>
                    </label>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ color: C.textDim, fontSize: 11, marginBottom: 6 }}>Other Parties Involved (select all that apply)</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {["Known Associates","Family Members","Business Partners","Third-Party Intermediaries","Unknown Counterparties","Correspondent Banks","Legal / Professional Advisors"].map(o => (
                        <button key={o} onClick={() => toggleInv("otherParties", o)}
                          style={{ padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace", border: `1px solid ${inv.otherParties.includes(o) ? C.accent : C.border}`, background: inv.otherParties.includes(o) ? `${C.accent}18` : "transparent", color: inv.otherParties.includes(o) ? C.accent : C.textMid }}>
                          {o}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div style={{ height: 1, background: C.border }} />

                {/* WHAT */}
                <div>
                  <div style={{ color: C.gold, fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 10 }}>WHAT — Activity &amp; Red Flags</div>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ color: C.textDim, fontSize: 11, marginBottom: 6 }}>Activity Type</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {["Cash Transactions","Wire Transfers","Cryptocurrency","Trade-Based ML","Real Estate","Loan-Back Scheme","Commingling","Structuring / Smurfing","Shell Company Activity","Investment Activity"].map(o => (
                        <button key={o} onClick={() => toggleInv("activityType", o)}
                          style={{ padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace", border: `1px solid ${inv.activityType.includes(o) ? C.gold : C.border}`, background: inv.activityType.includes(o) ? `${C.gold}18` : "transparent", color: inv.activityType.includes(o) ? C.gold : C.textMid }}>
                          {o}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ color: C.textDim, fontSize: 11, marginBottom: 6 }}>Red Flags Observed</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {["Unusual Transaction Amounts","No Apparent Business Purpose","Inconsistent with Customer Profile","Rapid Fund Movement / Layering","Third-Party Deposits","Unexplained Wealth / Source of Funds","Adverse Media","PEP Nexus","Round-Dollar Transactions","Structuring Below Threshold","Frequent Reversals","Geographic Anomaly"].map(o => (
                        <button key={o} onClick={() => toggleInv("redFlags", o)}
                          style={{ padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace", border: `1px solid ${inv.redFlags.includes(o) ? C.danger : C.border}`, background: inv.redFlags.includes(o) ? `${C.danger}18` : "transparent", color: inv.redFlags.includes(o) ? C.danger : C.textMid }}>
                          {o}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: C.textDim, fontSize: 11, marginBottom: 6 }}>Payment Methods</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {["Cash","Wire Transfer","Cryptocurrency","Cheque","Money Order","Mobile Payment","Debit / Credit Card","SWIFT","Hawala / Informal Value Transfer"].map(o => (
                        <button key={o} onClick={() => toggleInv("paymentMethods", o)}
                          style={{ padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace", border: `1px solid ${inv.paymentMethods.includes(o) ? C.gold : C.border}`, background: inv.paymentMethods.includes(o) ? `${C.gold}18` : "transparent", color: inv.paymentMethods.includes(o) ? C.gold : C.textMid }}>
                          {o}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div style={{ height: 1, background: C.border }} />

                {/* WHERE */}
                <div>
                  <div style={{ color: C.success, fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 10 }}>WHERE — Jurisdictions &amp; Accounts</div>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ color: C.textDim, fontSize: 11, marginBottom: 6 }}>Jurisdictions Involved</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {["High-Risk Jurisdiction (FATF)","Non-Cooperative Jurisdiction","Offshore Financial Centre","Sanctioned Country","Tax Haven","Domestic","EU / EEA","US","Multiple Jurisdictions"].map(o => (
                        <button key={o} onClick={() => toggleInv("jurisdictions", o)}
                          style={{ padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace", border: `1px solid ${inv.jurisdictions.includes(o) ? C.success : C.border}`, background: inv.jurisdictions.includes(o) ? `${C.success}18` : "transparent", color: inv.jurisdictions.includes(o) ? C.success : C.textMid }}>
                          {o}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: C.textDim, fontSize: 11, marginBottom: 6 }}>Account Types Implicated</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {["Personal Current Account","Business / Corporate Account","Correspondent Banking","VASP / Crypto Wallet","Offshore Account","Trust Account","Savings Account","Investment Account","Loan Account"].map(o => (
                        <button key={o} onClick={() => toggleInv("accountTypes", o)}
                          style={{ padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace", border: `1px solid ${inv.accountTypes.includes(o) ? C.success : C.border}`, background: inv.accountTypes.includes(o) ? `${C.success}18` : "transparent", color: inv.accountTypes.includes(o) ? C.success : C.textMid }}>
                          {o}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div style={{ height: 1, background: C.border }} />

                {/* WHEN */}
                <div>
                  <div style={{ color: C.textDim, fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 10, color: "#a78bfa" }}>WHEN — Timing &amp; Frequency</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <span style={{ color: C.textDim, fontSize: 11 }}>Timing Pattern</span>
                      <select value={inv.timingPattern} onChange={e => setInv(p => ({ ...p, timingPattern: e.target.value }))} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, padding: "7px 10px", fontFamily: "'DM Mono', monospace" }}>
                        <option value="">— Select —</option>
                        {["Single Event","Daily Recurrence","Weekly Recurrence","Monthly Recurrence","Seasonal / Periodic","Irregular / Random","Accelerating Pattern","End-of-Period Clustering"].map(o => <option key={o}>{o}</option>)}
                      </select>
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <span style={{ color: C.textDim, fontSize: 11 }}>Transaction Frequency</span>
                      <select value={inv.frequency} onChange={e => setInv(p => ({ ...p, frequency: e.target.value }))} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, padding: "7px 10px", fontFamily: "'DM Mono', monospace" }}>
                        <option value="">— Select —</option>
                        {["One-Time","2–5 Transactions","6–10 Transactions","11–20 Transactions","21–50 Transactions","50+ Transactions"].map(o => <option key={o}>{o}</option>)}
                      </select>
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <span style={{ color: C.textDim, fontSize: 11 }}>Duration of Concern</span>
                      <select value={inv.duration} onChange={e => setInv(p => ({ ...p, duration: e.target.value }))} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, padding: "7px 10px", fontFamily: "'DM Mono', monospace" }}>
                        <option value="">— Select —</option>
                        {["< 1 Week","1–4 Weeks","1–3 Months","3–6 Months","6–12 Months","> 1 Year","Ongoing"].map(o => <option key={o}>{o}</option>)}
                      </select>
                    </label>
                  </div>
                </div>

                <div style={{ height: 1, background: C.border }} />

                {/* WHY */}
                <div>
                  <div style={{ color: C.danger, fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 10 }}>WHY — Motive &amp; Purpose</div>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ color: C.textDim, fontSize: 11, marginBottom: 6 }}>Suspected Motive(s)</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {["Tax Evasion","Sanctions Evasion","Fraud Proceeds","Drug Trafficking","Human Trafficking / Modern Slavery","Corruption / Bribery","Terrorism Financing","Cybercrime / Ransomware","Insider Dealing","Proliferation Financing","Unknown"].map(o => (
                        <button key={o} onClick={() => toggleInv("suspectedMotive", o)}
                          style={{ padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace", border: `1px solid ${inv.suspectedMotive.includes(o) ? C.danger : C.border}`, background: inv.suspectedMotive.includes(o) ? `${C.danger}18` : "transparent", color: inv.suspectedMotive.includes(o) ? C.danger : C.textMid }}>
                          {o}
                        </button>
                      ))}
                    </div>
                  </div>
                  <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <span style={{ color: C.textDim, fontSize: 11 }}>Suspected Purpose of Funds / Activity</span>
                    <select value={inv.suspectedPurpose} onChange={e => setInv(p => ({ ...p, suspectedPurpose: e.target.value }))} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, padding: "7px 10px", fontFamily: "'DM Mono', monospace" }}>
                      <option value="">— Select —</option>
                      {["Investment","Business Operations","Personal Expenses","Property Purchase","Loan Repayment","Trade Finance","Asset Concealment","Layering / Integration","No Apparent Legitimate Purpose","Unclear / Unable to Determine"].map(o => <option key={o}>{o}</option>)}
                    </select>
                  </label>
                </div>

                {/* Build Narrative Button */}
                <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 4 }}>
                  <button
                    onClick={buildNarrativeDraft}
                    style={{ padding: "9px 22px", borderRadius: 7, border: "none", cursor: "pointer", background: C.accent, color: C.bg, fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 700 }}
                  >↓ Build Narrative Draft</button>
                </div>
              </div>
            ) : (
              <div style={{ color: C.textDim, fontSize: 12, lineHeight: 1.6 }}>
                Use the guided 5W framework — <strong style={{ color: C.textMid }}>Who, What, Where, When, Why</strong> — to structure your investigation analysis. Click expand to begin, then generate a narrative draft below.
              </div>
            )}
          </Section>

          {/* ── Case Narrative ── */}
          <Section title={t('caseDetail.sectionNarrative')}>
            <div style={{ color: C.textDim, fontSize: 11, marginBottom: 10, lineHeight: 1.6 }}>
              {t('caseDetail.narrativeHint')}
            </div>
            <textarea
              value={narrative}
              onChange={e => setNarrative(e.target.value)}
              placeholder={t('caseDetail.narrativePlaceholder', { entity: caseData.entity })}
              rows={7}
              style={{
                width: "100%", background: C.bg,
                border: `1px solid ${narrative.trim() ? C.borderLight : C.border}`,
                borderRadius: 8, color: C.text, fontSize: 13, padding: "12px 14px",
                fontFamily: "'DM Mono', monospace", outline: "none", resize: "vertical",
                boxSizing: "border-box", lineHeight: 1.7, transition: "border 0.15s",
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8, gap: 10, alignItems: "center" }}>
              {narrativeSaved && (
                <span style={{ color: C.success, fontSize: 11, fontFamily: "'DM Mono', monospace" }}>{t('caseDetail.narrativeSaved')}</span>
              )}
              <button
                onClick={saveNarrative}
                disabled={!narrative.trim()}
                style={{
                  padding: "7px 18px", borderRadius: 6, border: "none",
                  cursor: narrative.trim() ? "pointer" : "not-allowed",
                  background: narrative.trim() ? C.accent : C.border,
                  color: narrative.trim() ? C.bg : C.textDim,
                  fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 700,
                }}
              >{t('caseDetail.saveNarrative')}</button>
            </div>
          </Section>

          {/* ── Investigation Sources ── */}
          <InvestigationSources caseId={caseData.id} company={company} currentUser={currentUser} />

          {/* ── External Research / OSINT ── */}
          <Section title={t('caseDetail.sectionExternalResearch')}>
            <ExternalResearch
              subjectName={caseData.entity}
              subjectType={caseData.type?.toLowerCase().includes("business") ? "Business" : "Individual"}
              subjectCountry={caseData.country}
            />
          </Section>

          {/* ── AI Compliance Guidance ── */}
          <Section title={t('caseDetail.sectionAiGuidance')}>
            <div style={{ color: C.textDim, fontSize: 11, marginBottom: 12, lineHeight: 1.6 }}>
              {t('caseDetail.aiGuidanceHint')}
            </div>

            {/* Suggested prompts — shown before first message */}
            {aiMessages.length === 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
                {[
                  "What red flags should I look for?",
                  "Draft a SAR narrative for this case",
                  "What regulations apply to this transaction type?",
                  "What additional due diligence do you recommend?",
                  "Is this country high-risk under FATF guidelines?",
                ].map(p => (
                  <button key={p} onClick={() => setAiInput(p)} style={{
                    padding: "5px 10px", borderRadius: 5, cursor: "pointer",
                    background: C.accentGlow, border: `1px solid ${C.accent}40`,
                    color: C.accent, fontSize: 11, fontFamily: "'DM Mono', monospace",
                  }}>{p}</button>
                ))}
              </div>
            )}

            {/* Message thread */}
            {aiMessages.length > 0 && (
              <div style={{ maxHeight: 380, overflowY: "auto", marginBottom: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                {aiMessages.map((msg, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
                    <div style={{
                      maxWidth: "88%",
                      background: msg.role === "user" ? `${C.accent}14` : C.surfaceAlt,
                      border: `1px solid ${msg.role === "user" ? C.accent + "40" : C.border}`,
                      borderRadius: 9, padding: "10px 13px",
                      color: msg.role === "user" ? C.accent : C.text,
                      fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap",
                      fontFamily: msg.role === "user" ? "'DM Mono', monospace" : "inherit",
                    }}>
                      {msg.content}
                    </div>
                  </div>
                ))}
                {aiLoading && (
                  <div style={{ color: C.textDim, fontSize: 12, fontFamily: "'DM Mono', monospace" }}>{t('ai.thinking')}</div>
                )}
                <div ref={aiEndRef} />
              </div>
            )}

            {/* Input row */}
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                value={aiInput}
                onChange={e => setAiInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && sendAiMessage()}
                placeholder={t('caseDetail.aiInputPlaceholder')}
                disabled={aiLoading}
                style={{
                  flex: 1, background: C.bg, border: `1px solid ${C.border}`,
                  borderRadius: 7, padding: "9px 13px", color: C.text, fontSize: 12,
                  fontFamily: "'DM Mono', monospace", outline: "none",
                }}
                onFocus={e => e.target.style.borderColor = C.accent}
                onBlur={e => e.target.style.borderColor = C.border}
              />
              <button
                onClick={sendAiMessage}
                disabled={aiLoading || !aiInput.trim()}
                style={{
                  padding: "9px 16px", borderRadius: 7, border: "none",
                  background: aiInput.trim() && !aiLoading ? C.accent : C.border,
                  color: aiInput.trim() && !aiLoading ? C.bg : C.textDim,
                  fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 700,
                  cursor: aiInput.trim() && !aiLoading ? "pointer" : "not-allowed",
                  whiteSpace: "nowrap",
                }}
              >{t('ai.send')}</button>
            </div>
          </Section>
        </div>

        {/* Right column */}
        <div>
          <DocumentsPanel
            caseId={caseData.id}
            companyId={company?.id}
            currentUser={currentUser}
            locked={isLockedForMe || isComplete}
            onDocsChange={setCaseDocsMeta}
          />

          <AssignPanel
            caseData={caseData}
            team={team}
            currentUser={currentUser}
            onAssign={handleAssign}
            locked={isLockedForMe || isComplete}
          />

          {/* Decision panel — only shown when case is open and not complete */}
          {!isComplete && !isPendingReview && (
            <DecisionPanel
              caseData={caseData}
              team={team}
              currentUser={currentUser}
              company={company}
              onSubmitForReview={handleSubmitForReview}
              onClosed={(result) => {
                setCaseData(prev => ({ ...prev, status: "closed", closed_at: result.closed_at }));
                onCaseUpdated?.({ ...caseData, status: "closed", closed_at: result.closed_at });
                setTimeout(() => onBack?.(), 1200);
              }}
              onEscalated={(result) => {
                setCaseData(prev => ({ ...prev, status: "escalated", closed_at: result.closed_at }));
                onCaseUpdated?.({ ...caseData, status: "escalated", closed_at: result.closed_at });
                setTimeout(() => onBack?.(), 1200);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}